import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { Icon, Link, LinkSize, LinkTarget, Text } from '@atlassian/learning-components';

import { ContentItem } from 'src/types/contentful/base-content';
import { ContentType } from 'src/types/contentful/content-type';
import { getContentLink, getContentDescription, getContentLinkTarget } from 'src/pages/utils/utils';
import { AssociatedContent } from 'src/graphql/queries/GetContentHub';
import { transformLearningPathUrl } from 'src/utils/pathUtils';

export interface ContentBlockItemProps {
  content: AssociatedContent[number];
  onLearnMoreLinkClick: (cardTitle: string, link?: string, target?: LinkTarget) => void;
  linkSize?: LinkSize;
}

export const ContentBlockItem: React.FC<ContentBlockItemProps> = ({
  content,
  onLearnMoreLinkClick,
  linkSize = LinkSize.MEDIUM,
}) => {
  const contentItem = content as ContentItem;
  const contentLink = getContentLink(contentItem);
  const contentDescription = getContentDescription(contentItem);

  const target = getContentLinkTarget(contentItem);
  const isExternalLink = target === '_blank';
  let updatedLink = contentLink;

  if (content.__typename === ContentType.ASSOCIATED_CONTENT) {
    updatedLink = transformLearningPathUrl(contentLink);
  }

  return (
    <div css={associatedContentStyles} data-testid="content-block-associated-card">
      <div css={linkContainerStyles}>
        <Link
          label={content.title}
          link={updatedLink}
          target={target}
          onClick={() => onLearnMoreLinkClick(content.title, updatedLink, target)}
          size={linkSize}
        >
          <Text>{content.title}</Text>
        </Link>
        <span css={isExternalLink && externalLinkIconStyles}>
          <Icon
            type="arrow-right"
            size={linkSize === LinkSize.MEDIUM ? 'small' : 'medium'}
            color={token('color.icon.selected')}
          />
        </span>
      </div>
      {contentDescription && <p css={contentDescriptionStyles}>{contentDescription}</p>}
    </div>
  );
};

const associatedContentStyles = css({
  width: '100%',
  margin: '0 auto',
  padding: `${token('space.100')} 0`,

  [media.below.md]: {
    maxWidth: 'unset',
  },
});

const contentDescriptionStyles = css({
  overflow: 'hidden',
  color: token('color.text.subtle'),
  textOverflow: 'ellipsis',

  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: token('font.weight.regular'),
  lineHeight: '18px',
  marginTop: 0,

  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: '1',
  alignSelf: 'stretch',
});

const linkContainerStyles = css({
  columnGap: token('space.050'),
  display: 'flex',
  alignItems: 'center',
});

const externalLinkIconStyles = css({
  transform: 'rotate(315deg)',
  display: 'inline-block',
});
